<script setup lang="ts">
import FormScaffold from '@legacy/components/common/form/FormScaffold.vue';
import Sport from '@legacy/js/dto/offering/Sport';
import Error from '@legacy/js/model/Error.js';
import SportRepository from '@legacy/js/repository/SportsRepository';
import { Archive, ArchiveRestore, Edit, Trash } from 'lucide';
import { computed, onMounted, ref } from 'vue';
import { SphIcon } from '@/components/base/icon';
import { SphSpinner } from '@/components/base/spinner';
import { useI18n } from '@/composables/i18n';
import { useNotifications } from '@/composables/notifications';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = defineProps<{
    federationId: number
}>();

const { i18n } = useI18n();
const { notify } = useNotifications();

const loading = ref(false);
const pending = ref(false);
const sports = ref<Sport[]>([]);
const markedForDeletion = ref<Sport[]>([]);
const markedForArchive = ref<Sport[]>([]);
const markedForUnarchive = ref<Sport[]>([]);
const edited = ref<Sport[]>([]);
const error = ref(new Error());

const tDeleteColour = computed(() => i18n.t('Farbe löschen'));

function load() {
    loading.value = true;
    SportRepository.findAllWithArchived(props.federationId)
        .then((result: Sport[]) => {
            sports.value = result;
        })
        .catch(() => notify.error('error.loadingData'))
        .finally(() => {
            loading.value = false;
        });
}

function save() {
    pending.value = true;
    SportRepository.bulkUpdate(props.federationId, sports.value)
        .then(() => {
            load();
            error.value = new Error();
            notify.success('saveChanges.success');
        })
        .catch((e: Error) => {
            error.value = e;
            notify.error('saveChanges.error');
        })
        .finally(() => {
            pending.value = false;
        });
}

function addSport() {
    const sport = Sport.create();
    sports.value.push(sport);
    markEdited(sport);
}

function removeSport(sport: Sport) {
    sports.value = sports.value.filter((element) => element !== sport);
}

function markForDeletion(sport: Sport) {
    markedForDeletion.value.push(sport);
    sport.delete = true;
    sport.isMarked = true;
}

function unmarkForDeletion(sport: Sport) {
    markedForDeletion.value = markedForDeletion.value.filter((item) => item !== sport);
    sport.delete = false;
    sport.isMarked = false;
}

function markForArchive(sport: Sport) {
    markedForArchive.value.push(sport);
    sport.isArchived = true;
    sport.isMarked = true;
}

function unmarkForArchive(sport: Sport) {
    markedForArchive.value = markedForArchive.value.filter((item) => item !== sport);
    sport.isArchived = false;
    sport.isMarked = false;
}

function markForUnarchive(sport: Sport) {
    markedForUnarchive.value.push(sport);
    sport.isArchived = false;
    sport.isMarked = true;
}

function unmarkForUnarchive(sport: Sport) {
    markedForUnarchive.value = markedForUnarchive.value.filter((item) => item !== sport);
    sport.isArchived = true;
    sport.isMarked = false;
}

function markEdited(sport: Sport) {
    edited.value.push(sport);
}

function isMarkEdited(sport: Sport) {
    return edited.value.includes(sport);
}

function isMarkedForDeletion(sport: Sport) {
    return markedForDeletion.value.includes(sport);
}

function isMarkedForArchive(sport: Sport) {
    return markedForArchive.value.includes(sport);
}

function isMarkedForUnarchive(sport: Sport) {
    return markedForUnarchive.value.includes(sport);
}

function isMarked(sport: Sport) {
    return isMarkedForDeletion(sport)
        || isMarkedForArchive(sport)
        || isMarkedForUnarchive(sport);
}

function showErrorMessage(index: number) {
    return error.value.messageForField(`sports[${index}][name]`);
}

onMounted(() => {
    load();
});
</script>

<template>
    <div>
        <div class="kgr-row">
            {{ i18n.t('Hier können diverse Sportarten angelegt werden. Zur Verfügung stehen diese dann im Kursmodul, E-Mail-Formular und für die Übungsleiterlizenzen.') }}
        </div>
        <FormScaffold
            :with-back-button="false"
            :pending="pending"
            @primary-btn-clicked="save"
        >
            <SphSpinner v-if="loading" />
            <table
                v-else
                class="kgr-table kgr-table--striped kgr-table--block table-groups kgr-row"
            >
                <thead>
                    <tr class="kgr-table__row">
                        <th class="kgr-table__cell--headline"></th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ i18n.t('club.options.sport') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ i18n.t('Bearbeiten') }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(sport, index) in sports"
                        :key="index"
                        class="kgr-table__row"
                    >
                        <td :style="`background-color:${sport.colour ? sport.colour : ''}; width: 6px;`"></td>
                        <td
                            v-if="isMarkEdited(sport)"
                            class="kgr-table__cell"
                        >
                            <div class="kgr-inputGroup kgr-postfix kgr-postfix--top">
                                <div class="kgr-postfix__fixed">
                                    <input
                                        v-model="sport.colour"
                                        type="color"
                                        class="kgr-input"
                                        style="width: 40px;"
                                    />
                                </div>
                                <SphIcon
                                    v-show="sport.colour"
                                    :icon="Trash"
                                    size="sm"
                                    class="deleteColour"
                                    :title="tDeleteColour"
                                    @click="sport.colour = null"
                                />
                                <input
                                    v-model="sport.name"
                                    type="text"
                                    maxlength="255"
                                    class="kgr-input"
                                    placeholder="Name"
                                />
                            </div>
                            <div class="kgr-inputGroup__text kgr-inputGroup__text--error">
                                {{ showErrorMessage(index) }}
                            </div>
                        </td>
                        <td
                            v-else
                            class="kgr-table__cell"
                            :class="sport.isArchived ? 'kgr-color kgr-color--gray' : ''"
                        >
                            {{ sport.name }}
                        </td>
                        <td class="kgr-table__cell">
                            <span
                                class="kgr-hidden kgr-hidden--fadeInOnUnhidden"
                                :class="{ 'is-unhidden': !isMarked(sport) }"
                            >
                                <button
                                    v-if="sport.id"
                                    class="kgr-table__action"
                                    :title="i18n.t('bearbeiten')"
                                    @click="markEdited(sport)"
                                >
                                    <SphIcon :icon="Edit" />
                                </button>

                                <!-- delete button active -->
                                <button
                                    v-if="sport.isDeletable"
                                    class="kgr-table__action"
                                    :title="sport.id ? i18n.t('löschen') : undefined"
                                    @click="sport.id ? markForDeletion(sport) : removeSport(sport)"
                                >
                                    <SphIcon :icon="Trash" />
                                </button>

                                <!-- delete button disabled -->
                                <button
                                    v-if="!sport.isDeletable"
                                    class="kgr-table__action"
                                    :title="sport.reasonForNotDeletable"
                                    disabled
                                >
                                    <SphIcon
                                        :icon="Trash"
                                        class="kgr-icon--light"
                                        disabled
                                    />
                                </button>

                                <!-- archive button active -->
                                <button
                                    v-if="sport.id && sport.isArchivable"
                                    class="kgr-table__action"
                                    :title="i18n.t('archivieren')"
                                    @click="markForArchive(sport)"
                                >
                                    <SphIcon :icon="Archive" />
                                </button>

                                <!-- archive button disabled -->
                                <button
                                    v-if="sport.id && !sport.isArchivable && !sport.isArchived"
                                    class="kgr-table__action"
                                    :title="sport.reasonForNotArchivable"
                                    disabled
                                >
                                    <SphIcon
                                        :icon="Archive"
                                        disabled
                                        class="kgr-icon--light"
                                    />
                                </button>

                                <!-- unarchive button active -->
                                <button
                                    v-if="sport.id && sport.isArchived"
                                    class="kgr-table__action"
                                    :title="i18n.t('reaktivieren')"
                                    @click="markForUnarchive(sport)"
                                >
                                    <SphIcon
                                        :icon="ArchiveRestore"
                                    />
                                </button>
                            </span>
                            <a
                                v-if="isMarkedForDeletion(sport)"
                                class="undoRemoveRowIcon kgr-link kgr-link--unobtrusive"
                                @click="unmarkForDeletion(sport)"
                            >{{ i18n.t('Löschen rückgängig') }}</a>
                            <a
                                v-if="isMarkedForArchive(sport)"
                                class="undoRemoveRowIcon kgr-link kgr-link--unobtrusive"
                                @click="unmarkForArchive(sport)"
                            >{{ i18n.t('Archivieren rückgängig') }}</a>
                            <a
                                v-if="isMarkedForUnarchive(sport)"
                                class="undoRemoveRowIcon kgr-link kgr-link--unobtrusive"
                                @click="unmarkForUnarchive(sport)"
                            >{{ i18n.t('Reaktivieren rückgängig') }}</a>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div class="kgr-text kgr-text--rightAligned">
                <div
                    class="kgr-button kgr-button--accent"
                    @click="addSport()"
                >
                    {{ i18n.t('Neue Sportart hinzufügen') }}
                </div>
            </div>
        </FormScaffold>
    </div>
</template>

<style scoped>
.deleteColour {
    cursor: pointer;
    position: relative;
    top: 10px;
    margin: 5px;
}
</style>
